import { useEffect } from 'react';
import { create } from 'zustand';
import { onSessionChange, registerUserScopedReset } from './session-read';

/**
 * R11: what THIS device did with an incoming call (the seeker's side of the
 * app-wide poll), and whether the seeker's call screen is up.
 *
 *  - handled: joined, "Not now", or run out here, per session id. The sheet
 *    never shows that session again even if a poll still lists it (dedupe).
 *    Kept in sessionStorage like the ring's, so a reload does not bring back
 *    a sheet this tab answered; every read and write is guarded.
 *  - callScreens: how many seeker call screens are up AND polling the seat
 *    (SeekerFlow in a polling state, the group tab with a request up).
 *    While one is, that screen opens the call on its own, so the sheet stays
 *    out of the way, and its seat reads say so (call_screen=1) so the server
 *    keeps the sheet off every other tab and device too (#1000 review 5).
 */
/**
 * RING-PUSH: a tap on an incoming-call notification, waiting for its call to
 * reach the sheet. 'open': the body was tapped (the sheet just shows);
 * 'join' / 'not_now': the notification's own buttons, which the sheet runs
 * through its own Join / Not now as soon as it shows that session. Only the
 * named session's sheet takes it; a session that is already over never
 * shows, so its tap is never run.
 */
export interface PushCallTap {
  sessionId: number;
  action: 'open' | 'join' | 'not_now';
}

interface IncomingCallState {
  handled: Record<number, true>;
  callScreens: number;
  pushTap: PushCallTap | null;
  markHandled: (sessionId: number) => void;
  setPushTap: (tap: PushCallTap) => void;
  /** The sheet ran (or dropped) the tap for this session. */
  consumePushTap: (sessionId: number) => void;
  /**
   * #1009 item 4: a tap seen before the user is signed in (a cold start, the
   * sign-in bounce) waits here until ListenerRingHost runs it, once. Kept
   * out of `reset` on purpose: the sign-in that it waits for IS a session
   * change. Harmless across accounts: only the named session's own sheet
   * ever takes a tap, and only the account it rings for gets that sheet.
   */
  holdPushTap: (tap: PushCallTap) => void;
  /** The held tap, removed (null when none). */
  takeHeldPushTap: () => PushCallTap | null;
  reset: () => void;
}

let heldPushTap: PushCallTap | null = null;

/**
 * The tap a call push's landing carries (notification-router
 * incomingCallRoute: `call` = the session, `call_action` = a button), or
 * null when the address is not one.
 */
export function callTapFromSearch(search: string): PushCallTap | null {
  const params = new URLSearchParams(search);
  const sessionId = Number(params.get('call'));
  if (!Number.isInteger(sessionId) || sessionId <= 0) return null;
  const action = params.get('call_action');
  return { sessionId, action: action === 'join' || action === 'not_now' ? action : 'open' };
}

const HANDLED_KEY = 'empathease.incoming.handled';
const HANDLED_KEEP = 50;

function loadHandled(): Record<number, true> {
  try {
    const raw = sessionStorage.getItem(HANDLED_KEY);
    const ids: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(ids)) return {};
    return Object.fromEntries(
      ids.filter((id): id is number => Number.isInteger(id) && id > 0).map((id) => [id, true]),
    ) as Record<number, true>;
  } catch {
    return {};
  }
}

function saveHandled(handled: Record<number, true>): void {
  try {
    const ids = Object.keys(handled).map(Number).slice(-HANDLED_KEEP);
    if (ids.length === 0) sessionStorage.removeItem(HANDLED_KEY);
    else sessionStorage.setItem(HANDLED_KEY, JSON.stringify(ids));
  } catch {
    // Storage blocked or full: the in-memory set still works for this load.
  }
}

export const useIncomingCallStore = create<IncomingCallState>((set) => ({
  handled: loadHandled(),
  callScreens: 0,
  pushTap: null,
  setPushTap: (tap) => set({ pushTap: tap }),
  consumePushTap: (sessionId) =>
    set((s) => (s.pushTap?.sessionId === sessionId ? { pushTap: null } : {})),
  holdPushTap: (tap) => {
    heldPushTap = tap;
  },
  takeHeldPushTap: () => {
    const tap = heldPushTap;
    heldPushTap = null;
    return tap;
  },
  markHandled: (sessionId) =>
    set((s) => {
      const handled: Record<number, true> = { ...s.handled, [sessionId]: true };
      saveHandled(handled);
      return { handled };
    }),
  reset: () => {
    saveHandled({});
    set({ handled: {}, pushTap: null });
  },
}));

/**
 * The SeekerFlow states in which it polls the seat (and so opens a call that
 * lands): its fallback-poll effect's list. Idle (null) and the post-call
 * states poll nothing (#1000 review 1).
 */
export function isSeekerCallScreenState(state: string | null | undefined): boolean {
  return (
    state === 'MATCHING' ||
    state === 'WAITING' ||
    state === 'FALLBACK_ACTIVITY' ||
    state === 'CONNECTED'
  );
}

/**
 * Mounted by a screen that opens the seeker's call on its own (SeekerFlow;
 * the group Empathy tab while the viewer's request is up): the sheet yields
 * to it rather than offer a second way into the same call.
 */
export function useSeekerCallScreen(active = true): void {
  useEffect(() => {
    if (!active) return;
    useIncomingCallStore.setState((s) => ({ callScreens: s.callScreens + 1 }));
    return () => useIncomingCallStore.setState((s) => ({ callScreens: s.callScreens - 1 }));
  }, [active]);
}

registerUserScopedReset(() => useIncomingCallStore.getState().reset());

// A held tap waits through a sign-in (null → A) but not past a sign-out: a
// session that ends takes its held tap with it.
onSessionChange((_next, previous) => {
  if (previous !== null) heldPushTap = null;
});
